import type { Editor } from '../editor/editor';
import type { History } from '../editor/history';
import { MAX_PAGES, type PageSize } from '../editor/pageFormat';
import { DEFAULT_BLEED_MM } from '../print/rules';
import { pageThumb, readyThumb } from './pageThumbs';
import { $, $in, setPressed } from './dom';
import { checkDocSize, mmToPx, PRESETS, presetPx, sizeLabel } from './docSheet';
import { closeSheets, isOpen, openSheet } from './sheets';
import { toast } from './toast';

/*
 * Pages of the open design:
 *   switcher over the work area   ‹  หน้า 2 / 5  ›   (the chip opens the page list)
 *   "หน้าทั้งหมด" panel           preview of every page; tap = open it; duplicate, add, move, delete the open page
 * Only the open page is on the canvas (src/editor/history.ts); every change is one undo step.
 * On someone else's design (read-only) the pages can be looked at but not changed.
 */
export function initPagesSheet(ed: Editor, history: History, opts: { canEdit: () => boolean }) {
  const grid = $('pgGrid'), msg = $('pgMsg');
  const prev = $('pgPrev') as HTMLButtonElement, next = $('pgNext') as HTMLButtonElement, chip = $('pgChip');
  let armedDelete = false;
  let armTimer: ReturnType<typeof setTimeout> | undefined;
  let paintRun = 0;

  const say = (text: string) => { msg.textContent = text; msg.hidden = !text; };

  /* ---------- ขนาดหน้าใหม่: the open page's (default), a preset, or one typed in ---------- */
  let newSize = 'same';            // 'same' | preset index | 'own'
  let ownUnit: 'mm' | 'px' = 'mm';
  const sizeBox = $('pgSize');
  sizeBox.innerHTML = `<button data-v="same" aria-pressed="true">เท่าหน้านี้</button>`
    + PRESETS.map((p, i) => `<button data-v="${i}" aria-pressed="false">${p.label}</button>`).join('')
    + `<button data-v="own" aria-pressed="false">กำหนดเอง</button>`;
  function syncSize() {
    setPressed('pgSize', b => b.dataset.v === newSize);
    setPressed('pgUnit', b => b.dataset.v === ownUnit);
    $('pgOwn').hidden = $('pgUnit').hidden = newSize !== 'own';
  }
  sizeBox.onclick = e => { const b = (e.target as Element).closest('button'); if (!b) return; newSize = b.dataset.v!; syncSize(); };
  $('pgUnit').onclick = e => { const b = (e.target as Element).closest('button'); if (!b) return; ownUnit = b.dataset.v as typeof ownUnit; syncSize(); };

  /**
   * the new page's size and bleed: undefined = as the open page; a Thai reason when the typed size cannot be used.
   * The bleed is the open page's; a print size (mm) on a page without bleed gets the standard 3 mm (as in ขนาดงาน).
   */
  function chosenSize(): PageSize | undefined | string {
    if (newSize === 'same') return undefined;
    let w: number, h: number, forPrint: boolean;
    if (newSize === 'own') {
      const a = parseFloat($in('pgW').value), b = parseFloat($in('pgH').value);
      if (!(a > 0) || !(b > 0)) return 'กรอกความกว้างและความสูงของหน้าใหม่เป็นตัวเลขที่มากกว่า 0';
      w = ownUnit === 'mm' ? mmToPx(a) : Math.round(a); h = ownUnit === 'mm' ? mmToPx(b) : Math.round(b); forPrint = ownUnit === 'mm';
    } else {
      const p = PRESETS[+newSize];
      ({ w, h } = presetPx(p)); forPrint = !!p.mm;
    }
    const err = checkDocSize(w, h);
    if (err) return err;
    const bleed = ed.doc.bleedMm;
    return { w, h, bleedMm: forPrint && !((bleed ?? 0) > 0) ? DEFAULT_BLEED_MM : bleed };
  }
  /** add a blank page after `after` at the chosen size */
  function addBlank(after: number) {
    const size = chosenSize();
    if (typeof size === 'string') { report(size); return; }
    if (report(history.addPage(after, undefined, size))) {
      toast(`เพิ่มหน้าว่างเป็นหน้า ${after + 2} แล้ว${size ? ` ขนาด ${sizeLabel(size.w, size.h)}` : ''}`);
    }
  }
  const report = (why: string | null) => { if (why) { toast(why, true); say(why); } return !why; };

  /** the switcher over the work area */
  function sync() {
    const n = history.pageCount(), cur = history.currentPage();
    chip.textContent = `หน้า ${cur + 1} / ${n}`;
    prev.hidden = next.hidden = n <= 1;
    prev.disabled = cur === 0;
    next.disabled = cur === n - 1;
    if (isOpen('shPages')) void paint();
  }

  function disarm() { armedDelete = false; clearTimeout(armTimer); $('pgDel').textContent = 'ลบหน้านี้'; }

  /** the panel: one card per page (preview drawn one at a time to keep memory low), then the actions */
  async function paint() {
    const run = ++paintRun;
    const n = history.pageCount(), cur = history.currentPage(), editable = opts.canEdit();
    $('pgTitle').textContent = `หน้าทั้งหมด · ${n} หน้า`;
    $('pgSel').textContent = `หน้าที่เปิดอยู่: หน้า ${cur + 1}`;
    $('pgActs').hidden = !editable;
    grid.style.setProperty('--pg-ratio', String(ed.doc.w / ed.doc.h));
    const sizes = history.pageSizes(), mixed = history.mixed();
    syncSize();
    ($('pgUp') as HTMLButtonElement).disabled = cur === 0;
    ($('pgDown') as HTMLButtonElement).disabled = cur === n - 1;
    ($('pgDel') as HTMLButtonElement).disabled = n <= 1;
    ($('pgDup') as HTMLButtonElement).disabled = ($('pgAdd') as HTMLButtonElement).disabled = n >= MAX_PAGES;

    grid.textContent = '';
    const cards: Array<{ img: HTMLElement; json: string; size: PageSize }> = [];
    for (let k = 0; k < n; k++) {
      const card = document.createElement('button');
      card.className = 'pgcard';
      card.setAttribute('aria-current', String(k === cur));
      card.setAttribute('aria-label', `เปิดหน้า ${k + 1}`);
      const json = history.pageJson(k), size = sizes[k];
      card.style.setProperty('--pg-ratio', String(size.w / size.h));   // each page its own shape (หน้าหลายขนาด)
      const ready = readyThumb(json, size, 200);
      const img: HTMLElement = ready
        ? Object.assign(document.createElement('img'), { src: ready, alt: '' })
        : Object.assign(document.createElement('span'), { className: 'pgph' });
      const name = history.pageName(k);
      card.append(img, Object.assign(document.createElement('span'), { textContent: `หน้า ${k + 1}${name ? ' · ' + name : ''}${mixed ? ' · ' + sizeLabel(size.w, size.h) : ''}` }));
      card.onclick = () => { disarm(); say(''); if (report(history.goPage(k))) { /* repainted by sync() after the page opens */ } };
      grid.append(card);
      if (!ready) cards.push({ img, json, size });
    }
    if (editable && n < MAX_PAGES) {
      const add = document.createElement('button');
      add.className = 'pgcard';
      add.setAttribute('aria-label', 'เพิ่มหน้าว่างต่อท้าย (ขนาดตาม "ขนาดหน้าใหม่")');
      add.append(Object.assign(document.createElement('span'), { className: 'pgadd', textContent: '+ เพิ่มหน้า' }),
        Object.assign(document.createElement('span'), { textContent: 'หน้าว่าง' }));
      add.onclick = () => { disarm(); say(''); addBlank(history.pageCount() - 1); };
      grid.append(add);
    }
    // previews: one page at a time; a page that cannot be drawn says so on its card
    for (const c of cards) {
      if (run !== paintRun) return;
      try {
        const url = await pageThumb(c.json, c.size, ed.doc, 200);
        if (run === paintRun) c.img.replaceWith(Object.assign(document.createElement('img'), { src: url, alt: '' }));
      } catch (e) {
        if (run === paintRun) c.img.replaceWith(Object.assign(document.createElement('span'), {
          className: 'pgph', title: e instanceof Error ? e.message : String(e), textContent: 'แสดงตัวอย่างไม่ได้'
        }));
      }
    }
  }

  /* ---------- turning pages: ‹ › and a two-finger swipe (src/editor/gestures.ts) ---------- */
  const wrap = ed.canvas.getElement().parentElement as HTMLElement;   // Fabric's .canvas-container
  const still = matchMedia('(prefers-reduced-motion: reduce)');
  const SLIDE_PX = 48, HALF_MS = 100;                                  // out 0.1 s + in 0.1 s
  let turning = false;

  /** the previous (-1) or next (+1) page, sliding the work area out and the new page in; nothing past the ends */
  function turn(dir: -1 | 1) {
    const to = history.currentPage() + dir;
    if (to < 0 || to >= history.pageCount() || turning) return;
    turning = true;
    const animate = !still.matches && typeof wrap.animate === 'function';
    const out = animate
      ? wrap.animate([{ transform: 'none', opacity: 1 }, { transform: `translateX(${-dir * SLIDE_PX}px)`, opacity: 0 }],
        { duration: HALF_MS, easing: 'ease-in', fill: 'forwards' })
      : null;
    const open = () => {
      const why = history.goPage(to, () => {
        // the new page is on the canvas (its pictures loaded): slide it in from the other side
        const back = animate
          ? wrap.animate([{ transform: `translateX(${dir * SLIDE_PX}px)`, opacity: 0 }, { transform: 'none', opacity: 1 }],
            { duration: HALF_MS, easing: 'ease-out' })
          : null;
        out?.cancel();
        if (back) back.finished.catch(() => {}).finally(() => { turning = false; });
        else turning = false;
      });
      if (why) { out?.cancel(); turning = false; report(why); }
    };
    if (out) out.finished.then(open, open); else open();
  }

  prev.onclick = () => turn(-1);
  next.onclick = () => turn(1);
  chip.onclick = () => { ed.canvas.discardActiveObject(); ed.canvas.requestRenderAll(); say(''); disarm(); openSheet('shPages'); void paint(); };

  /* ---------- actions on the open page ---------- */
  $('pgDup').onclick = () => { disarm(); say(''); if (report(history.addPage(history.currentPage(), history.currentPage()))) toast(`ทำซ้ำเป็นหน้า ${history.currentPage() + 1} แล้ว`); };
  $('pgAdd').onclick = () => { disarm(); say(''); addBlank(history.currentPage()); };
  $('pgUp').onclick = () => { disarm(); say(''); report(history.movePage(history.currentPage(), -1)); };
  $('pgDown').onclick = () => { disarm(); say(''); report(history.movePage(history.currentPage(), 1)); };
  $('pgDel').onclick = () => {
    say('');
    if (!armedDelete) {
      armedDelete = true;
      $('pgDel').textContent = `แตะอีกครั้งเพื่อลบหน้า ${history.currentPage() + 1}`;
      armTimer = setTimeout(disarm, 3500);
      return;
    }
    const gone = history.currentPage() + 1;
    disarm();
    if (report(history.deletePage(history.currentPage()))) toast(`ลบหน้า ${gone} แล้ว กดเลิกทำถ้าต้องการกลับไป`);
  };

  return { sync, paint, turn, close: () => { if (isOpen('shPages')) closeSheets(); } };
}
